import { useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  Loader2,
  AlertTriangle,
  FileText,
  Shield,
  Target,
  CheckCircle,
  Eye,
  Bus,
  Car,
  Bike,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import policyService from "../../api/PolicyService";
import ConfirmationModal from "../../components/ConfirmationModal";
import useAccountStatusCheck from "../../hooks/useAccountStatusCheck";
import { StepperNav } from "../../components/policy/StepperNav";

import {
  StepInsuranceType,
  StepBasicInfo,
  StepCoverage,
  StepBenefitsExclusions,
  StepTermsAndConditions,
  StepReview,
  isFixedDurationPolicy,
  validateFixedCoveragePrices,
  buildFixedCoverageDurations,
} from "../../components/PolicyFormComponents";

export default function CreatePolicy() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.trp_usr);
  const { isBlocked, AccountStatusModal } = useAccountStatusCheck();

  const getPolicyName = useCallback(
    (insuranceType, subcategory = "") => {
      let companyName = user?.company_name || "Company";
      const insuranceRegex = /\s+insurance\s*$/i;
      companyName = companyName.replace(insuranceRegex, "").trim();

      if (insuranceType === "tripsecure") {
        return "TripSecure Road Trip Insurance";
      } else if (insuranceType === "life") {
        return `${companyName} Life Insurance`;
      } else if (insuranceType === "travel") {
        return `${companyName} Travel Insurance`;
      } else if (insuranceType === "general") {
        if (subcategory) {
          const subcategoryNames = {
            health_insurance: "Health Insurance",
            home_insurance: "Home Insurance",
            accident_insurance: "Accident Insurance",
            fire_insurance: "Fire Insurance",
            marine_insurance: "Marine Insurance",
            liability_insurance: "Liability Insurance",
          };
          const subcategoryName =
            subcategoryNames[subcategory] || subcategory.replace(/_/g, " ");
          return `${companyName} ${subcategoryName}`;
        }
        return `${companyName} General Insurance`;
      }
      return "TripSecure Road Trip Insurance";
    },
    [user]
  );

  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(() => {
    const initialInsuranceType = "tripsecure";
    return {
      insuranceType: initialInsuranceType,
      policyName: getPolicyName(initialInsuranceType, ""),
      policyType: "",
      description: "",
      shortDescription: "",

      geography: "",
      regionalRoutes: {},
      internationalRoutes: {},
      sevenDayPrice: "",
      oneMonthPrice: "",

      subcategory: "",
      packages: [],

      insuranceCoverageList: [],
      exclusionsList: [],

      // Provider-managed fields for Travel, Life, and General Insurance
      claimProcessSteps: [],
      termsAndConditions: "",
      termsInputType: "text", // "text" or "url"
      eligibilityCriteria: [],

      max_claims: 3,
    };
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const steps = [
    {
      id: 1,
      title: "Insurance Type",
      subtitle: "Select insurance category",
      icon: Shield,
      color: "blue",
    },
    {
      id: 2,
      title: "Basic Info",
      subtitle: "Policy fundamentals",
      icon: FileText,
      color: "green",
    },
    {
      id: 3,
      title: "Coverage",
      subtitle: "Routes & pricing",
      icon: Shield,
      color: "purple",
    },
    {
      id: 4,
      title: "Benefits & Exclusions",
      subtitle: "What's covered",
      icon: Target,
      color: "orange",
    },
    {
      id: 5,
      title: "Terms & Conditions",
      subtitle: "Policy terms",
      icon: FileText,
      color: "teal",
    },
    {
      id: 6,
      title: "Review",
      subtitle: "Final approval",
      icon: Eye,
      color: "indigo",
    },
  ];

  const policyTypes = [
    {
      id: "daily_commuter",
      name: "Daily Commuter",
      description:
        "Perfect for everyday urban transportation and short-distance travel",
      icon: Bus,
      color: "blue",
      disabled: false,
      features: [
        "7-day and 1-month coverage",
        "Everyday urban travel",
        "Multi-modal transport",
        "City-wide protection",
      ],
    },
    {
      id: "intercity_travel",
      name: "Intercity Travel",
      description:
        "Comprehensive coverage for long-distance journeys and highway travel",
      icon: Car,
      color: "green",
      disabled: false,
      features: [
        "Regional & International routes",
        "Flexible route selection",
        "Highway incidents coverage",
        "Extended journey protection",
      ],
    },
    {
      id: "okada_rider",
      name: "Motor Rider",
      description: "Specialized protection for motorcycle transport and riders",
      icon: Bike,
      color: "orange",
      disabled: false,
      features: [
        "7-day and 1-month coverage",
        "Motorcycle-specific protection",
        "High-risk areas covered",
        "Competitive pricing",
      ],
    },
  ];

  const hasFormData = Object.values(formData).some((value) => {
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.length > 0;
    return false;
  });

  const updateFormData = useCallback(
    (field, value) => {
      setFormData((prev) => {
        const newData = { ...prev, [field]: value };

        if (field === "insuranceType") {
          const previousInsuranceType = prev.insuranceType;
          const newInsuranceType = value;

          // If switching to Life, General, or Travel Insurance, clear tripsecure-specific fields
          if (
            newInsuranceType === "life" ||
            newInsuranceType === "general" ||
            newInsuranceType === "travel"
          ) {
            newData.policyType = "";
            newData.geography = "";
            newData.regionalRoutes = {};
            newData.internationalRoutes = {};
            newData.sevenDayPrice = "";
            newData.oneMonthPrice = "";
          }

          // If switching to Travel Insurance, clear Life/General-specific fields
          if (newInsuranceType === "travel") {
            newData.packages = [];
            newData.subcategory = "";
          }

          // If switching to TripSecure Insurance, clear package fields
          if (newInsuranceType === "tripsecure") {
            newData.packages = [];
            newData.subcategory = "";
          }

          // Clear benefits and exclusions when switching insurance types
          if (
            previousInsuranceType &&
            previousInsuranceType !== newInsuranceType
          ) {
            newData.insuranceCoverageList = [];
            newData.exclusionsList = [];
          }

          // Update policy name based on insurance type
          newData.policyName = getPolicyName(
            newInsuranceType,
            newData.subcategory
          );
        }

        // Handle subcategory changes for General Insurance
        if (field === "subcategory" && prev.insuranceType === "general") {
          newData.policyName = getPolicyName(prev.insuranceType, value);
        }

        // Handle terms input type changes - clear data when switching types
        if (field === "termsInputType") {
          const previousType = prev.termsInputType;
          const newType = value;
          if (previousType !== newType) {
            // Clear the terms data when switching input types
            if (newType === "url") {
              // Clear text if switching to URL
              newData.termsAndConditions = "";
            }
          }
        }

        // Handle geography changes with smart benefits/exclusions management
        if (field === "geography") {
          const previousGeography = prev.geography;
          const newGeography = value;

          if (
            previousGeography === "both" &&
            (newGeography === "regional" || newGeography === "international")
          ) {
            // Keep only benefits/exclusions that match the new geography type
            newData.insuranceCoverageList =
              prev.insuranceCoverageList?.filter(
                (item) => item.type === newGeography || item.type === "general"
              ) || [];
            newData.exclusionsList =
              prev.exclusionsList?.filter(
                (item) => item.type === newGeography || item.type === "general"
              ) || [];
          } else if (
            (previousGeography === "regional" ||
              previousGeography === "international") &&
            newGeography === "both"
          ) {
            newData.insuranceCoverageList = (
              prev.insuranceCoverageList || []
            ).map((item) => ({
              ...item,
              type: item.type || previousGeography,
            }));
            newData.exclusionsList = (prev.exclusionsList || []).map(
              (item) => ({
                ...item,
                type: item.type || previousGeography,
              })
            );
          } else if (
            (previousGeography === "regional" &&
              newGeography === "international") ||
            (previousGeography === "international" &&
              newGeography === "regional")
          ) {
            newData.insuranceCoverageList = [];
            newData.exclusionsList = [];
          } else {
            newData.insuranceCoverageList = [];
            newData.exclusionsList = [];
          }
        }

        return newData;
      });

      // Clear validation error for this field
      setValidationErrors((prev) => {
        if (prev[field]) {
          const { [field]: removed, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    },
    [getPolicyName]
  );

  // Navigation with unsaved changes warning
  const handleNavigation = useCallback(
    (path) => {
      if (hasFormData && !submitSuccess) {
        setPendingNavigation(path);
        setShowUnsavedModal(true);
        return;
      }
      navigate(path);
    },
    [hasFormData, submitSuccess, navigate]
  );

  // Handle unsaved changes modal confirmation
  const handleUnsavedConfirm = () => {
    setShowUnsavedModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  // Handle unsaved changes modal cancellation
  const handleUnsavedCancel = () => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  // Validation
  const validateStep = (step) => {
    const errors = {};

    switch (step) {
      case 1: // Insurance Type
        if (!formData.insuranceType)
          errors.insuranceType = "Please select an insurance type";
        break;

      case 2: // Basic Info
        if (formData.insuranceType === "tripsecure") {
          if (!formData.policyType)
            errors.policyType = "Please select a policy type";
          const maxClaims = Number(formData.max_claims);
          if (!Number.isInteger(maxClaims) || maxClaims < 1 || maxClaims > 999) {
            errors.max_claims = "Max Claims is required (1–999)";
          }
        }
        if (formData.insuranceType === "general" && !formData.subcategory) {
          errors.subcategory = "Please select a subcategory";
        }
        break;

      case 3: // Coverage
        if (formData.insuranceType === "tripsecure") {
          if (formData.policyType === "intercity_travel") {
            if (!formData.geography) {
              errors.geography = "Please select a geographic coverage type";
            }

            // Check if at least one route is selected
            const hasRegionalRoute =
              formData.geography === "regional" ||
              formData.geography === "both";
            const hasInternationalRoute =
              formData.geography === "international" ||
              formData.geography === "both";

            const selectedRegionalRoutes = hasRegionalRoute
              ? Object.values(formData.regionalRoutes || {}).filter(
                  (r) => r.selected && r.price && parseFloat(r.price) >= 0.01
                )
              : [];

            const selectedInternationalRoutes = hasInternationalRoute
              ? Object.values(formData.internationalRoutes || {}).filter(
                  (r) => r.selected && r.price && parseFloat(r.price) >= 0.01
                )
              : [];

            if (
              selectedRegionalRoutes.length === 0 &&
              selectedInternationalRoutes.length === 0
            ) {
              errors.routes = "Please select and price at least one route";
            }

            // Validate regional route prices don't exceed max
            if (hasRegionalRoute) {
              const invalidRegionalPrices = Object.values(
                formData.regionalRoutes || {}
              ).filter((r) => r.selected && parseFloat(r.price) > 10);
              if (invalidRegionalPrices.length > 0) {
                errors.routes = "Regional route prices cannot exceed ₵10";
              }
            }

            // Validate international route prices don't exceed max
            if (hasInternationalRoute) {
              const invalidInternationalPrices = Object.values(
                formData.internationalRoutes || {}
              ).filter((r) => r.selected && parseFloat(r.price) > 20);
              if (invalidInternationalPrices.length > 0) {
                errors.routes = "International route prices cannot exceed ₵20";
              }
            }
          } else if (isFixedDurationPolicy(formData.policyType)) {
            validateFixedCoveragePrices(formData, errors);
          }
        } else if (
          formData.insuranceType === "life" ||
          formData.insuranceType === "general" ||
          formData.insuranceType === "travel"
        ) {
          // Validate packages with per-package policy fields
          if (!formData.packages || formData.packages.length === 0) {
            errors.packages = "Please add at least one package";
          } else {
            // Validate each package has all required fields including per-package policy fields
            const invalidPackages = formData.packages.filter((pkg) => {
              // Basic fields
              const productName = pkg.productName?.trim() || "";
              const hasProductName = productName.length > 0;

              const isQuoteOnly = !!pkg.is_quote_only;
              const premiumStr = String(pkg.premium || "").trim();
              const premiumValue = premiumStr ? parseFloat(premiumStr) : NaN;
              const hasPremium = isQuoteOnly || (!isNaN(premiumValue) && premiumValue > 0);

              const premiumDuration = pkg.premiumDuration || "monthly";
              const hasPremiumDuration = isQuoteOnly || ["daily", "weekly", "monthly", "yearly"].includes(premiumDuration);

              const durationValueStr = String(pkg.durationValue || "").trim();
              const durationValue = durationValueStr
                ? parseFloat(durationValueStr)
                : NaN;
              const hasDurationValue =
                !isNaN(durationValue) && durationValue > 0;

              const durationUnit =
                pkg.durationUnit ||
                (formData.insuranceType === "travel" ? "hours" : "years");
              let hasDurationUnit = false;
              if (formData.insuranceType === "travel" || formData.insuranceType === "life" || formData.insuranceType === "general") {
                hasDurationUnit = [
                  "hours",
                  "hour",
                  "day",
                  "days",
                  "week",
                  "weeks",
                  "month",
                  "months",
                  "years",
                ].includes(durationUnit);
              } else {
                hasDurationUnit =
                  durationUnit === "years" || durationUnit === "months";
              }

              const description = pkg.description?.trim() || "";
              const hasDescription = description.length > 0;

              // Per-package policy fields validation
              const hasBenefits =
                (pkg.insurance_coverage_list || []).length > 0;
              const hasValidBenefits = (
                pkg.insurance_coverage_list || []
              ).every((b) => b.title?.trim() && b.description?.trim());

              const hasEligibility =
                (pkg.eligibility_criteria || []).filter(
                  (c) => c && c.trim() !== ""
                ).length > 0;

              const hasClaimSteps = (pkg.claim_process_steps || []).length > 0;
              const hasValidClaimSteps = (pkg.claim_process_steps || []).every(
                (s) =>
                  s.title?.trim() &&
                  s.description?.trim() &&
                  s.timeframe?.trim()
              );

              const hasTerms = pkg.terms && pkg.terms.trim() !== "";
              const hasValidTerms =
                !hasTerms ||
                (pkg.termsInputType === "url"
                  ? (() => {
                      try {
                        new URL(pkg.terms);
                        return true;
                      } catch {
                        return false;
                      }
                    })()
                  : true);

              const maxClaims = Number(pkg.max_claims);
              const hasMaxClaims = Number.isInteger(maxClaims) && maxClaims >= 1 && maxClaims <= 999;

              return (
                !hasProductName ||
                !hasPremium ||
                !hasPremiumDuration ||
                !hasDurationValue ||
                !hasDurationUnit ||
                !hasDescription ||
                !hasMaxClaims ||
                !hasBenefits ||
                !hasValidBenefits ||
                !hasEligibility ||
                !hasClaimSteps ||
                !hasValidClaimSteps ||
                !hasTerms ||
                !hasValidTerms
              );
            });

            if (invalidPackages.length > 0) {
              const durationUnitsText =
                formData.insuranceType === "travel" || formData.insuranceType === "life" || formData.insuranceType === "general"
                  ? "hours, hour, day, days, week, weeks, month, months, or years"
                  : "years or months";
              errors.packages = `Each package must have: Product name, Premium (> 0) or "Quote-Based Only", Payment Term (daily/weekly/monthly/yearly), Package Duration (> 0, unit: ${durationUnitsText}), Description, Max Claims (1–999), at least 1 Benefit with title & description, at least 1 Eligibility criterion, at least 1 Claim step with title/description/timeframe, and Terms & Conditions`;
            }
          }
        }
        break;

      case 4: // Benefits & Exclusions (TripSecure only)
        // For TripSecure, validate global benefits/exclusions
        if (formData.insuranceType === "tripsecure") {
          if (
            !formData.insuranceCoverageList ||
            formData.insuranceCoverageList.length === 0
          ) {
            errors.benefits = "Please add at least one coverage benefit";
          } else {
            // Check if benefits have required fields
            const invalidBenefits = formData.insuranceCoverageList.filter(
              (b) => !b.title?.trim() || !b.description?.trim()
            );
            if (invalidBenefits.length > 0) {
              errors.benefits =
                "All benefits must have a title and description";
            }
          }
        }
        // For non-TripSecure, this step is skipped (validation is per-package in step 3)
        break;

      case 5: // Terms and Conditions (TripSecure only)
        // For TripSecure, terms are handled globally with defaults
        // For non-TripSecure, this step is skipped (terms are per-package in step 3)
        break;

      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateAllSteps = () => {
    const isTripSecure = formData.insuranceType === "tripsecure";
    // For TripSecure, validate steps 1-4 (skip step 5 - Terms)
    // For non-TripSecure, validate steps 1-3 (steps 4 & 5 are per-package now)
    const maxStep = isTripSecure ? 4 : 3;
    for (let i = 1; i <= maxStep; i++) {
      if (!validateStep(i)) {
        return false;
      }
    }
    return true;
  };

  // Scroll to top helper
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // Navigation
  const nextStep = () => {
    if (validateStep(currentStep)) {
      const isTripSecure = formData.insuranceType === "tripsecure";
      setCurrentStep((prev) => {
        if (isTripSecure) {
          // TripSecure: Skip step 5 (Terms)
          if (prev === 4) {
            return 6; // Go directly to Review
          }
        } else {
          // Non-TripSecure: Skip steps 4 & 5 (Benefits & Terms are per-package)
          if (prev === 3) {
            return 6; // Go directly to Review after Coverage
          }
        }
        return Math.min(prev + 1, steps.length);
      });
      scrollToTop();
    }
  };

  const prevStep = () => {
    const isTripSecure = formData.insuranceType === "tripsecure";
    setCurrentStep((prev) => {
      if (isTripSecure) {
        // TripSecure: Skip step 5 (Terms) when going back
        if (prev === 6) {
          return 4; // Go back to Benefits & Exclusions
        }
      } else {
        // Non-TripSecure: Skip steps 4 & 5 when going back
        if (prev === 6) {
          return 3; // Go back to Coverage
        }
      }
      return Math.max(prev - 1, 1);
    });
    scrollToTop();
  };

  const goToStep = (step) => {
    if (step <= currentStep || validateStep(currentStep)) {
      const isTripSecure = formData.insuranceType === "tripsecure";
      let actualStep = step;

      if (isTripSecure) {
        // Map display step to actual step for TripSecure (skip step 5)
        actualStep = step === 5 ? 6 : step;
      } else {
        // Map display step to actual step for non-TripSecure (skip steps 4 & 5)
        if (step === 4) {
          actualStep = 6; // Map to Review
        }
      }

      setCurrentStep(actualStep);
      scrollToTop();
    }
  };

  // Calculate progress
  const progress = useMemo(() => {
    const isTripSecure = formData.insuranceType === "tripsecure";
    let visibleSteps;
    let currentDisplayStep;

    if (isTripSecure) {
      // TripSecure: Skip step 5 (Terms)
      visibleSteps = steps.filter((step) => step.id !== 5);
      currentDisplayStep = currentStep === 6 ? 5 : currentStep;
    } else {
      // Non-TripSecure: Skip steps 4 & 5 (Benefits & Terms are per-package)
      visibleSteps = steps.filter((step) => step.id !== 4 && step.id !== 5);
      currentDisplayStep = currentStep === 6 ? 4 : currentStep;
    }

    return ((currentDisplayStep - 1) / (visibleSteps.length - 1)) * 100;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, formData.insuranceType]);

  const renderCurrentStep = () => {
    const isTripSecure = formData.insuranceType === "tripsecure";

    switch (currentStep) {
      case 1:
        return (
          <StepInsuranceType
            formData={formData}
            updateFormData={updateFormData}
            validationErrors={validationErrors}
          />
        );
      case 2:
        return (
          <StepBasicInfo
            formData={formData}
            updateFormData={updateFormData}
            policyTypes={policyTypes}
            validationErrors={validationErrors}
          />
        );
      case 3:
        return (
          <StepCoverage
            formData={formData}
            updateFormData={updateFormData}
            validationErrors={validationErrors}
          />
        );
      case 4:
        return (
          <StepBenefitsExclusions
            formData={formData}
            updateFormData={updateFormData}
            validationErrors={validationErrors}
          />
        );
      case 5:
        // Only show Terms step for non-TripSecure insurance
        if (isTripSecure) {
          return <StepReview formData={formData} policyTypes={policyTypes} />;
        }
        return (
          <StepTermsAndConditions
            formData={formData}
            updateFormData={updateFormData}
            validationErrors={validationErrors}
          />
        );
      case 6:
        return <StepReview formData={formData} policyTypes={policyTypes} />;
      default:
        return (
          <StepInsuranceType
            formData={formData}
            updateFormData={updateFormData}
            validationErrors={validationErrors}
          />
        );
    }
  };

  const transformFormDataToPayload = () => {
    let coverageDurationsInHours = [];
    let geography = formData.geography || "";

    // Handle TripSecure Insurance
    if (
      formData.insuranceType === "tripsecure" &&
      formData.policyType === "intercity_travel"
    ) {
      const hasRegionalRoute = geography === "regional" || geography === "both";
      const hasInternationalRoute =
        geography === "international" || geography === "both";

      if (hasRegionalRoute) {
        Object.entries(formData.regionalRoutes || {}).forEach(
          ([destination, data]) => {
            if (data.selected && data.price) {
              const REGIONAL_ROUTES = [
                { destination: "Volta Region", hours: 6 },
                { destination: "Eastern Region", hours: 4 },
                { destination: "Ashanti Region", hours: 8 },
                { destination: "Central Region", hours: 6 },
                { destination: "Western Region", hours: 8 },
                { destination: "Upper West Region", hours: 20 },
              ];
              const routeConfig = REGIONAL_ROUTES.find(
                (r) => r.destination === destination
              );
              if (routeConfig) {
                coverageDurationsInHours.push({
                  hours: routeConfig.hours,
                  price: parseFloat(data.price),
                  display_unit: "hours",
                  display_value: routeConfig.hours,
                  route_destination: destination,
                  route_type: "regional",
                });
              }
            }
          }
        );
      }

      if (hasInternationalRoute) {
        Object.entries(formData.internationalRoutes || {}).forEach(
          ([destination, data]) => {
            if (data.selected && data.price) {
              const INTERNATIONAL_ROUTES = [
                { destination: "Togo", hours: 8 },
                { destination: "Benin", hours: 12 },
                { destination: "Nigeria", hours: 24 },
                { destination: "Ivory Coast", hours: 15 },
                { destination: "Burkina Faso", hours: 20 },
              ];
              const routeConfig = INTERNATIONAL_ROUTES.find(
                (r) => r.destination === destination
              );
              if (routeConfig) {
                coverageDurationsInHours.push({
                  hours: routeConfig.hours,
                  price: parseFloat(data.price),
                  display_unit: "hours",
                  display_value: routeConfig.hours,
                  route_destination: destination,
                  route_type: "international",
                });
              }
            }
          }
        );
      }
    } else if (isFixedDurationPolicy(formData.policyType)) {
      coverageDurationsInHours = buildFixedCoverageDurations(formData);
      geography = "national";
    }

    // NOTE: For non-tripsecure types, coverageDurationsInHours will be assigned later

    // Transform benefits (insuranceCoverageList)
    const benefits = formData.insuranceCoverageList.map((benefit) => ({
      title: benefit.title?.trim() || "",
      description: benefit.description?.trim() || "",
      limit: benefit.limit?.trim() || "",
      ...(benefit.type && benefit.type !== "general"
        ? {
            coverage_type: benefit.type,
          }
        : {}),
    }));

    const exclusions = (formData.exclusionsList || [])
      .map((exclusion) => {
        const text = exclusion.text?.trim() || "";
        if (exclusion.type && exclusion.type !== "general") {
          return `[${exclusion.type.toUpperCase()}] ${text}`;
        }
        return text;
      })
      .filter((e) => e !== "");

    // Hardcoded defaults for TripSecure Insurance
    const defaultEligibilityCriteria = [
      "Must be 18 years or older",
      "Valid identification required",
      "Journey must originate from Greater Accra region",
    ];

    const defaultTerms =
      "This policy is governed by TripSecure's standard terms and conditions. Coverage is valid for the specified duration and route only.";

    const defaultClaimSteps = [
      {
        step_number: 1,
        title: "Report Incident",
        description:
          "Contact TripSecure support immediately after the incident",
        timeframe: "Within 24 hours",
        requirements: "Incident details and location",
      },
      {
        step_number: 2,
        title: "Submit Documentation",
        description: "Provide all required documents and evidence",
        timeframe: "Within 7 days",
        requirements:
          "Police report (if applicable), photos, witness statements",
      },
      {
        step_number: 3,
        title: "Claim Review",
        description: "TripSecure team reviews your claim",
        timeframe: "5-10 business days",
        requirements: "None",
      },
      {
        step_number: 4,
        title: "Claim Settlement",
        description: "Approved claims are processed for payment",
        timeframe: "2-5 business days after approval",
        requirements: "Valid bank account details",
      },
    ];

    // Get eligibility criteria - use form data for Travel, Life, and General Insurance
    // Always use hardcoded defaults for TripSecure
    const getEligibilityCriteria = () => {
      if (formData.insuranceType === "tripsecure") {
        // Always use hardcoded defaults for TripSecure
        return defaultEligibilityCriteria;
      }
      // Use provider-defined eligibility criteria for Travel, Life, and General
      // Return empty array if none provided (will use package-level criteria)
      const criteria = (formData.eligibilityCriteria || []).filter(
        (c) => c && c.trim() !== ""
      );
      return criteria.length > 0 ? criteria : [];
    };

    const eligibilityCriteria = getEligibilityCriteria();

    // Get terms and conditions - use form data for Travel, Life, and General Insurance
    // Always use hardcoded defaults for TripSecure
    const getTerms = () => {
      if (formData.insuranceType === "tripsecure") {
        // Always use hardcoded defaults for TripSecure
        return defaultTerms;
      }

      // Handle different input types for Travel, Life, and General
      const inputType = formData.termsInputType || "text";

      if (inputType === "url") {
        // Return the URL directly
        return (
          formData.termsAndConditions?.trim() ||
          "This policy is governed by standard terms and conditions. Coverage is valid for the specified duration and route only."
        );
      } else {
        // Text input type
        return (
          formData.termsAndConditions?.trim() ||
          "This policy is governed by standard terms and conditions. Coverage is valid for the specified duration and route only."
        );
      }
    };

    const terms = getTerms();

    // Get claim process - use form data for Travel, Life, and General Insurance
    // Always use hardcoded defaults for TripSecure
    const getClaimProcessSteps = () => {
      if (formData.insuranceType === "tripsecure") {
        // Always use hardcoded defaults for TripSecure
        return defaultClaimSteps;
      }
      // Use provider-defined claim process steps for Travel, Life, and General
      const steps = (formData.claimProcessSteps || []).filter(
        (step) =>
          step.title?.trim() &&
          step.description?.trim() &&
          step.timeframe?.trim()
      );
      if (steps.length > 0) {
        // Ensure step numbers are sequential
        return steps.map((step, index) => ({
          ...step,
          step_number: index + 1,
        }));
      }
      // Fallback for Travel/Life/General if no steps provided
      return [
        {
          step_number: 1,
          title: "Claim Process",
          description: "Contact support to initiate your claim process",
          timeframe: "As specified in policy",
          requirements: "Follow the process outlined in your policy",
        },
      ];
    };

    const claimProcessSteps = getClaimProcessSteps();

    // For non-tripsecure types, assign packages with per-package policy fields
    if (
      formData.insuranceType === "life" ||
      formData.insuranceType === "general" ||
      formData.insuranceType === "travel"
    ) {
      coverageDurationsInHours = (formData.packages || []).map((pkg, index) => {
        // Combine durationValue and durationUnit into a duration string
        const durationValue = pkg.durationValue || "";
        const durationUnit =
          pkg.durationUnit ||
          (formData.insuranceType === "travel" ? "hours" : "years");
        const duration = durationValue
          ? `${durationValue} ${durationUnit}`
          : "";

        // Package-level benefits
        const packageBenefits =
          Array.isArray(pkg.insurance_coverage_list) &&
          pkg.insurance_coverage_list.length > 0
            ? pkg.insurance_coverage_list.map((benefit) => ({
                title: benefit.title?.trim() || "",
                description: benefit.description?.trim() || "",
                limit: benefit.limit?.trim() || "",
                ...(benefit.type && benefit.type !== "general"
                  ? { coverage_type: benefit.type }
                  : {}),
              }))
            : Array.isArray(benefits) && benefits.length > 0
            ? benefits
            : [
                {
                  title: "Comprehensive Coverage",
                  description:
                    "Full coverage as per policy terms and conditions",
                  limit: "As per policy",
                },
              ];

        // Package-level exclusions
        const packageExclusions =
          Array.isArray(pkg.exclusions) && pkg.exclusions.length > 0
            ? (pkg.exclusions || [])
                .map((exclusion) => {
                  const text = exclusion?.text?.trim
                    ? exclusion.text.trim()
                    : typeof exclusion === "string"
                    ? exclusion.trim()
                    : "";
                  return text;
                })
                .filter((e) => e !== "")
            : Array.isArray(exclusions) && exclusions.length > 0
            ? exclusions
            : [
                "Pre-existing conditions not covered",
                "Intentional acts or illegal activities",
                "War, terrorism, or nuclear risks",
              ];

        // Package-level eligibility criteria (check both field name formats)
        const pkgEligibility = pkg.eligibility_criteria || pkg.eligibilityCriteria || [];
        const packageEligibilityCriteria =
          Array.isArray(pkgEligibility) &&
          pkgEligibility.filter((c) => c && c.trim() !== "").length > 0
            ? pkgEligibility
                .filter((c) => c && c.trim() !== "")
                .map((c) => c.trim())
            : (Array.isArray(eligibilityCriteria) && eligibilityCriteria.length > 0
                ? eligibilityCriteria
                : ["Must be 18 years or older"]);

        // Package-level terms (check both field name formats)
        const packageTerms = (() => {
          const inputType = pkg.termsInputType || "text";
          const value = (pkg.terms || pkg.termsAndConditions)?.trim();
          if (value && inputType === "url") {
            return value;
          }
          if (value && inputType === "text") {
            return value;
          }
          return terms || "This policy is governed by standard terms and conditions. Coverage is valid for the specified duration and route only.";
        })();

        // Package-level claim process steps (check both field name formats)
        const pkgClaimSteps = pkg.claim_process_steps || pkg.claimProcessSteps || [];
        const packageClaimProcessSteps =
          Array.isArray(pkgClaimSteps) &&
          pkgClaimSteps.filter(
            (step) =>
              step.title?.trim() &&
              step.description?.trim() &&
              step.timeframe?.trim()
          ).length > 0
            ? pkgClaimSteps
                .filter(
                  (step) =>
                    step.title?.trim() &&
                    step.description?.trim() &&
                    step.timeframe?.trim()
                )
                .map((step, idx) => ({
                  ...step,
                  step_number: idx + 1,
                  title: step.title?.trim() || "",
                  description: step.description?.trim() || "",
                  timeframe: step.timeframe?.trim() || "",
                  requirements: step.requirements || "",
                }))
            : (Array.isArray(claimProcessSteps) && claimProcessSteps.length > 0
                ? claimProcessSteps
                : [
                    {
                      step_number: 1,
                      title: "Claim Process",
                      description: "Contact support to initiate your claim process",
                      timeframe: "As specified in policy",
                      requirements: "Follow the process outlined in your policy",
                    },
                  ]);

        const quoteRequestFields = (pkg.quote_request_fields || []).map((f) => ({
          key: (f.key || "").trim(),
          label: (f.label || "").trim(),
          type: f.type || "text",
          required: !!f.required,
          ...(f.type === "select" && Array.isArray(f.options) ? { options: f.options } : f.type === "select" && f.options ? { options: typeof f.options === "string" ? f.options.split(",").map((s) => s.trim()).filter(Boolean) : [] } : {}),
        })).filter((f) => f.key && f.label);

        return {
          product_name: pkg.productName?.trim() || "",
          premium: pkg.is_quote_only ? 0 : (parseFloat(pkg.premium) || 0),
          premium_duration: pkg.premiumDuration || "monthly",
          duration: duration,
          description: pkg.description?.trim() || "",
          max_claims: Number(pkg.max_claims) >= 1 ? Number(pkg.max_claims) : 3,
          is_quote_only: !!pkg.is_quote_only,
          quote_request_fields: pkg.is_quote_only ? quoteRequestFields : [],
          // Per-package policy fields
          insurance_coverage_list: packageBenefits,
          exclusions: packageExclusions,
          eligibility_criteria: packageEligibilityCriteria,
          claim_process_steps: packageClaimProcessSteps,
          terms: packageTerms,
        };
      });
    }

    // Get default description based on insurance type
    const getDefaultDescription = () => {
      if (formData.description?.trim()) {
        return formData.description.trim();
      }

      switch (formData.insuranceType) {
        case "life":
          return "Comprehensive life insurance coverage providing financial security and protection for you and your loved ones. Benefits include death benefit, cash value accumulation, and flexible payment options.";
        case "general":
          return "Comprehensive general insurance coverage designed to protect against various risks and uncertainties. Our flexible packages offer tailored protection to meet your specific needs and provide peace of mind.";
        case "travel":
        default:
          return "Comprehensive travel insurance coverage for your journey. Protection includes medical expenses, trip cancellation, and emergency assistance.";
      }
    };

    const defaultDescription = getDefaultDescription();

    const defaultPolicyRules =
      "Standard TripSecure policy rules apply. Claims must be reported within 24 hours of incident. All documentation must be submitted within 7 days.";

    const defaultSupportContact = "support@tripsecure.com | +233 XXX XXX XXX";

    // Get the policy name based on insurance type
    const policyName = getPolicyName(
      formData.insuranceType,
      formData.subcategory
    );

    // Map insurance type to the correct format for backend
    const insuranceTypeMap = {
      tripsecure: "tripsecure_insurance",
      travel: "travel_insurance",
      life: "life_insurance",
      general: "general_insurance",
    };
    const insuranceType =
      insuranceTypeMap[formData.insuranceType] || "tripsecure_insurance";

    // Determine policy_type based on insurance type
    let policyType = formData.policyType || "";
    if (formData.insuranceType === "tripsecure") {
      // Keep the policy type as is (intercity_travel, okada_rider, etc.)
      policyType = formData.policyType || "";
    } else if (formData.insuranceType === "life") {
      policyType = "life_insurance";
    } else if (formData.insuranceType === "general") {
      policyType = formData.subcategory || "";
    } else if (formData.insuranceType === "travel") {
      policyType = "travel_insurance";
    }

    const shortDescription = insuranceType || "tripsecure_insurance";

    // For non-TripSecure types, provide policy-level values
    // Use first package's data if available, otherwise use dummy values
    const policyLevelBenefits =
      formData.insuranceType === "tripsecure"
        ? benefits
        : benefits.length > 0
        ? benefits
        : [
            {
              title: "Coverage as specified in package",
              description: "Refer to individual package details",
              limit: "As per package",
            },
          ];

    const policyLevelExclusions =
      formData.insuranceType === "tripsecure"
        ? exclusions
        : exclusions.length > 0
        ? exclusions
        : ["Refer to package-specific exclusions"];

    // For non-TripSecure, try to use first package's eligibility criteria if available
    const policyLevelEligibility =
      formData.insuranceType === "tripsecure"
        ? eligibilityCriteria
        : eligibilityCriteria.length > 0
        ? eligibilityCriteria
        : coverageDurationsInHours.length > 0 &&
          Array.isArray(coverageDurationsInHours[0].eligibility_criteria) &&
          coverageDurationsInHours[0].eligibility_criteria.length > 0
        ? coverageDurationsInHours[0].eligibility_criteria
        : ["Must be 18 years or older"];

    return {
      insurance_type: insuranceType,
      policy_name: policyName,
      policy_type: policyType,
      description: defaultDescription,
      short_description: shortDescription,

      // Coverage Details
      geography: geography,
      coverage_durations: coverageDurationsInHours,
      insurance_coverage_list: policyLevelBenefits,

      risk_level: "medium",
      eligibility_criteria: policyLevelEligibility,
      exclusions: policyLevelExclusions,

      // Terms & Conditions
      terms: terms,
      claim_process_steps: claimProcessSteps,
      support_contact: defaultSupportContact,
      policy_rules: defaultPolicyRules,

      // Settings: TripSecure uses policy-level max_claims; Life/General/Travel use per-package (in coverage_durations)
      max_claims: formData.insuranceType === "tripsecure"
        ? (Number(formData.max_claims) || 3)
        : (coverageDurationsInHours?.length > 0
            ? Math.max(...coverageDurationsInHours.map((d) => Number(d.max_claims) || 3))
            : 3),
      waiting_period: 0,

      has_policy_image: false,
      policy_image_base64: "",
      documents: [],
    };
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    if (!validateAllSteps()) {
      const firstErrorStep = Object.keys(validationErrors).length > 0 ? 1 : 1;
      setCurrentStep(firstErrorStep);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = transformFormDataToPayload();

      // Show loading state
      console.log("Submitting policy:", payload);

      const result = await policyService.createPolicy(payload);

      if (result.status) {
        setSubmitSuccess(true);

        // Show success message
        setTimeout(() => {
          navigate("/policies");
        }, 2000);
      } else {
        setSubmitError(result.message || "Failed to create policy");
      }
    } catch (error) {
      console.error("Policy creation error:", error);
      setSubmitError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      {/* Account Status Modal */}
      <AccountStatusModal />

      {/* Success Notification */}
      <AnimatePresence>
        {submitSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            className="fixed top-4 left-1/2 z-50 transform -translate-x-1/2"
          >
            <div className="flex items-center px-6 py-4 text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg">
              <CheckCircle className="mr-3 w-6 h-6" />
              <div>
                <div className="font-semibold">
                  Policy Created Successfully!
                </div>
                <div className="text-sm opacity-90">
                  Redirecting to policies page...
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Notification */}
      <AnimatePresence>
        {submitError && (
          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            className="fixed top-4 left-1/2 z-50 transform -translate-x-1/2"
          >
            <div className="flex items-center px-6 py-4 text-white bg-gradient-to-r from-red-500 to-pink-600 rounded-xl shadow-lg">
              <AlertTriangle className="mr-3 w-6 h-6" />
              <div>
                <div className="font-semibold">Creation Failed</div>
                <div className="text-sm opacity-90">{submitError}</div>
              </div>
              <button
                onClick={() => setSubmitError(null)}
                className="p-1 ml-4 rounded-full transition-colors hover:bg-white hover:bg-opacity-20"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={isBlocked ? "pointer-events-none opacity-30 blur-sm transition-all duration-300" : ""}>
        {/* Header */}
        <div className="px-4 pt-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-3"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex-shrink-0 p-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-md">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-semibold text-gray-900 truncate sm:text-xl">
                  Create New Policy
                </h1>
                <p className="text-xs text-gray-500 sm:text-sm">
                  Design and configure your insurance policy offering
                </p>
              </div>
            </div>

            <div className="flex flex-shrink-0 items-center gap-3">
              <div className="hidden text-right sm:block">
                <div className="text-xs font-medium text-gray-900 sm:text-sm">
                  Step {currentStep} of {steps.length}
                </div>
                <div className="text-xs text-gray-500">
                  {Math.round(progress)}% Complete
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleNavigation("/policies")}
                className="inline-flex flex-shrink-0 items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white rounded-lg shadow-sm transition-all duration-200 hover:shadow-md hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </motion.button>
            </div>
          </div>
        </motion.div>
        </div>

        <StepperNav
          steps={steps}
          currentStep={currentStep}
          goToStep={goToStep}
          insuranceType={formData.insuranceType}
          termsStepId={5}
        />

        {/* Main Content */}
        <div className="px-4 pt-4 pb-6 mx-auto max-w-8xl sm:px-6 lg:px-8">
        <div className="overflow-hidden relative bg-white rounded-2xl border border-gray-200 shadow-lg">
          {/* Loading Overlay */}
          <AnimatePresence>
            {isSubmitting && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex absolute inset-0 z-10 justify-center items-center bg-white bg-opacity-90 backdrop-blur-sm"
              >
                <div className="text-center">
                  <Loader2 className="mx-auto mb-4 w-8 h-8 text-blue-600 animate-spin" />
                  <div className="text-lg font-semibold text-gray-900">
                    Creating Your Policy...
                  </div>
                  <div className="mb-4 text-sm text-gray-600">
                    Please wait while we process your request
                  </div>
                  <div className="overflow-hidden w-64 h-2 bg-gray-200 rounded-full">
                    <motion.div
                      className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 2, ease: "easeInOut" }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="p-4 sm:p-6">
            <AnimatePresence mode="wait">{renderCurrentStep()}</AnimatePresence>
          </div>

          {/* Navigation Footer */}
          <div className="px-4 py-4 bg-gray-50 border-t border-gray-200 sm:px-6 lg:px-8 sm:py-6">
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium transition-all duration-200 ${
                  currentStep === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                <ArrowLeft className="mr-2 w-4 h-4" />
                Previous
              </motion.button>

              <div className="flex space-x-3">
                {currentStep === 6 ? (
                  <motion.button
                    whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={`flex items-center justify-center px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-white rounded-xl shadow-lg transition-all duration-200 w-full sm:w-auto ${
                      isSubmitting
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        <span className="hidden sm:inline">
                          Creating Policy...
                        </span>
                        <span className="sm:hidden">Creating...</span>
                      </>
                    ) : (
                      <>
                        <Check className="mr-2 w-4 h-4" />
                        <span className="hidden sm:inline">Create Policy</span>
                        <span className="sm:hidden">Create</span>
                      </>
                    )}
                  </motion.button>
                ) : (
                  <motion.button
                    whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    onClick={nextStep}
                    disabled={isSubmitting}
                    className={`flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 font-medium text-white rounded-xl shadow-lg transition-all duration-200 w-full sm:w-auto ${
                      isSubmitting
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    }`}
                  >
                    Next
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </motion.button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Unsaved Changes Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUnsavedModal}
        onClose={handleUnsavedCancel}
        onConfirm={handleUnsavedConfirm}
        title="Discard Changes"
        message="You have unsaved form data that will be lost if you leave this page. Are you sure you want to continue?"
        confirmText="Leave Page"
        cancelText="Stay Here"
        variant="warning"
      />
    </div>
  );
}
